import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js'
import { handleRealtimeCompetitorUpdate } from '@/composables/useCompetitors'
import { handleRealtimeAgentTaskUpdate } from '@/composables/useAgentTasks'
import { handleRealtimeUserAlertUpdate } from '@/composables/useUserAlerts'
import { handleRealtimeUserProfileUpdate } from '@/composables/useUserProfile'
import { handleRealtimeCompanyUpdate } from '@/composables/useCompany'

interface RealtimeListener {
  channel: RealtimeChannel
  unsubscribe: () => void
}

export const useRealtimeListeners = () => {
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const listeners = ref<Record<string, RealtimeListener>>({})

  const subscribedChannels = useSubscribedChannels()

  // Helper function to handle channel status changes with error handling
  const handleChannelStatus = (channelName: string, status: string) => {
    console.log(`${channelName} realtime status: ${status}`)
    subscribedChannels.value.channels[channelName] = status
    
    // Handle channel errors
    if (status === 'CHANNEL_ERROR') {
      console.error(`❌ Channel error detected for ${channelName}. Attempting to resubscribe...`)
      // Attempt to resubscribe after a short delay
      setTimeout(() => {
        const channelType = channelName.split('_')[0]
        const id = channelName.split('_').slice(1).join('_')
        
        console.log(`🔄 Attempting to resubscribe to ${channelName}`)
        
        switch (channelType) {
          case 'agent':
            if (channelName.includes('tasks')) {
              subscribeToAgentTasks(id)
            }
            break
          case 'user':
            if (channelName.includes('alerts')) {
              subscribeToUserAlerts(id)
            } else if (channelName.includes('profile')) {
              subscribeToUserProfile(id)
            }
            break
          case 'competitor':
            subscribeToCompetitorMonitoring(id)
            break
          case 'company':
            subscribeToCompany(id)
            break
        }
      }, 1000)
    }
    
    // Handle successful subscription
    if (status === 'SUBSCRIBED') {
      console.log(`✅ Successfully subscribed to ${channelName}`)
    }
  }

  // Subscribe to agent tasks changes
  const subscribeToAgentTasks = (companyId: string) => {
    console.log("Subscribing to agent tasks for company:", companyId)

    const channelName = `agent_tasks_${companyId}`
    
    // Don't create duplicate subscriptions
    if (listeners.value[channelName]) {
      console.log("Already subscribed to agent tasks for company:", companyId)
      return
    }

    console.log("Creating new channel:", channelName)
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_executions',
          filter: `company_id=eq.${companyId}`
        },
        handleRealtimeAgentTaskUpdate
      )
      .subscribe((status: string) => {
        handleChannelStatus(channelName, status)
      })

    listeners.value[channelName] = {
      channel,
      unsubscribe: () => {
        console.log(`Unsubscribing from ${channelName}`)
        channel.unsubscribe()
        delete listeners.value[channelName]
        delete subscribedChannels.value.channels[channelName]
      }
    }
  }

  // Subscribe to user alerts changes
  const subscribeToUserAlerts = (companyId: string) => {
    console.log("Subscribing to user alerts for company:", companyId)

    const channelName = `user_alerts_${companyId}`

    // Don't create duplicate subscriptions
    if (listeners.value[channelName]) {
      console.log("Already subscribed to user alerts for company:", companyId)
      return
    }

    console.log("Creating new channel:", channelName)
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_alerts',
          filter: `company_id=eq.${companyId}`
        },
        handleRealtimeUserAlertUpdate
      )
      .subscribe((status: string) => {
        handleChannelStatus(channelName, status)
      })

    listeners.value[channelName] = {
      channel,
      unsubscribe: () => {
        console.log(`Unsubscribing from ${channelName}`)
        channel.unsubscribe()
        delete listeners.value[channelName]
        delete subscribedChannels.value.channels[channelName]
      }
    }
  }

  // Subscribe to competitor monitoring changes
  const subscribeToCompetitorMonitoring = (companyId: string) => {
    console.log("Subscribing to competitor monitoring for company:", companyId)

    const channelName = `competitor_monitoring_${companyId}`
    
    // Don't create duplicate subscriptions
    if (listeners.value[channelName]) {
      console.log("Already subscribed to competitor monitoring for company:", companyId)
      return
    }

    console.log("Creating new channel:", channelName)
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'companies_monitoring',
          filter: `company_id=eq.${companyId}`
        },
        handleRealtimeCompetitorUpdate
      )
      .subscribe((status: string) => {
        handleChannelStatus(channelName, status)
      })

    listeners.value[channelName] = {
      channel,
      unsubscribe: () => {
        console.log(`Unsubscribing from ${channelName}`)
        channel.unsubscribe()
        delete listeners.value[channelName]
        delete subscribedChannels.value.channels[channelName]
      }
    }
  }

  // Subscribe to user profile changes
  const subscribeToUserProfile = (userId: string) => {
    console.log("Subscribing to user profile for user:", userId)

    const channelName = `user_profile_${userId}`

    // Don't create duplicate subscriptions
    if (listeners.value[channelName]) {
      console.log("Already subscribed to user profile for user:", userId)
      return
    }

    console.log("Creating new channel:", channelName)
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles',
          filter: `user_id=eq.${userId}`
        },
        handleRealtimeUserProfileUpdate
      )
      .subscribe((status: string) => {
        handleChannelStatus(channelName, status)
      })

    listeners.value[channelName] = {
      channel,
      unsubscribe: () => {
        console.log(`Unsubscribing from ${channelName}`)
        channel.unsubscribe()
        delete listeners.value[channelName]
        delete subscribedChannels.value.channels[channelName]
      }
    }

    console.log("listeners.value", listeners.value)
  }

  // Subscribe to company changes
  const subscribeToCompany = (companyId: string) => {
    console.log("Subscribing to company for company:", companyId)

    const channelName = `company_${companyId}`

    // Don't create duplicate subscriptions
    if (listeners.value[channelName]) {
      console.log("Already subscribed to company for company:", companyId)
      return
    }

    console.log("Creating new channel:", channelName)
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public',
          table: 'companies',
          filter: `id=eq.${companyId}`
        },
        handleRealtimeCompanyUpdate
      )
      .subscribe((status: string) => {
        handleChannelStatus(channelName, status)
      })

    listeners.value[channelName] = {
      channel,
      unsubscribe: () => {
        console.log(`Unsubscribing from ${channelName}`)
        channel.unsubscribe()
        delete listeners.value[channelName]
        delete subscribedChannels.value.channels[channelName]
      }
    }
  }

  // Helper function to resubscribe to all channels
  const resubscribeAllChannels = () => {
    console.log("🔄 Resubscribing to all channels after auth refresh...")
    
    // Get current subscriptions to recreate them
    const currentChannels = Object.keys(subscribedChannels.value.channels)
    
    // Clear existing listeners first
    removeAllListeners()
    
    // Resubscribe to each channel
    currentChannels.forEach(channelName => {
      const channelType = channelName.split('_')[0]
      const id = channelName.split('_').slice(1).join('_')
      
      setTimeout(() => {
        console.log(`🔄 Resubscribing to ${channelName}`)
        
        switch (channelType) {
          case 'agent':
            if (channelName.includes('tasks')) {
              subscribeToAgentTasks(id)
            }
            break
          case 'user':
            if (channelName.includes('alerts')) {
              subscribeToUserAlerts(id)
            } else if (channelName.includes('profile')) {
              subscribeToUserProfile(id)
            }
            break
          case 'competitor':
            subscribeToCompetitorMonitoring(id)
            break
          case 'company':
            subscribeToCompany(id)
            break
        }
      }, Math.random() * 1000) // Stagger resubscriptions to avoid overwhelming the server
    })
  }

  // Cleanup function to remove all listeners
  const removeAllListeners = () => {
    console.log("🧹 Cleaning up all realtime listeners")
    Object.values(listeners.value).forEach(listener => {
      listener.unsubscribe()
    })
    listeners.value = {}
    subscribedChannels.value.channels = {}
  }

  // Cleanup single listener
  const removeListener = (channelName: string) => {
    if (listeners.value[channelName]) {
      listeners.value[channelName].unsubscribe()
    }
  }

  return {
    subscribeToCompetitorMonitoring,
    subscribeToAgentTasks,
    subscribeToUserAlerts,
    subscribeToUserProfile,
    subscribeToCompany,
    removeListener,
    removeAllListeners,
    resubscribeAllChannels
  }
} 